import { useEffect } from 'react';
import { setMostRecentlyUpdatedModelPageContext, useSessionMostRecentlyUpdatedModelId } from '@/platform-services/session';
export const DELAY = 200;
export const FADE_DELAY = 2000;
// The nearest ancestor the user could scroll themselves: overflow auto/scroll
// and actually overflowing. overflow: hidden ancestors are skipped on purpose.
const findScrollableAncestor = (target) => {
    let element = target.parentElement;
    while (element) {
        const { overflowY } = getComputedStyle(element);
        if ((overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight) {
            return element;
        }
        element = element.parentElement;
    }
    return null;
};
// Not target.scrollIntoView(): that scrolls every ancestor, overflow: hidden
// ones included, and on the rules page it ran before the table had limited
// its own height — so it scrolled the page's fixed-height content container,
// taking the title, filter bar and horizontal scrollbar off screen until a
// reload. Deferred to a task of its own: the table sets its max-height in a
// layout effect, which React commits before any timer can run, so the table
// is scrollable by then. If nothing can scroll, nothing is scrolled.
const scrollIntoScrollableAncestor = (target) => setTimeout(() => {
    const container = findScrollableAncestor(target);
    if (container) {
        container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    }
});
export const highlightTarget = ({ target, className, resetFunction, scrollToHighlightedItem, delay = DELAY, fadeDelay = FADE_DELAY, }) => {
    const scrollTimeoutId = scrollToHighlightedItem ? scrollIntoScrollableAncestor(target) : undefined;
    target.classList.add(className);
    let shadowTimeoutId;
    const highlightingTimeoutId = setTimeout(() => {
        target.classList.remove(className);
        target.style.transition = `background-color ${fadeDelay}ms`;
        resetFunction();
        shadowTimeoutId = setTimeout(() => { target.style.transition = ''; }, fadeDelay);
    }, delay);
    return ({
        shadowTimeoutId,
        highlightingTimeoutId,
        scrollTimeoutId,
    });
};
export const useTableHighlighting = ({ tableRef, className = 'row--highlighted', resetFunction, scrollToHighlightedItem = true, }) => {
    const mostRecentlyUpdatedModel = useSessionMostRecentlyUpdatedModelId();
    const commonReset = () => setMostRecentlyUpdatedModelPageContext(null, null);
    const reset = resetFunction || commonReset;
    useEffect(() => {
        var _a;
        const target = (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`#row_${mostRecentlyUpdatedModel}`);
        if (target) {
            const { highlightingTimeoutId, shadowTimeoutId, scrollTimeoutId } = highlightTarget({
                target,
                className,
                resetFunction: reset,
                scrollToHighlightedItem,
            });
            return () => {
                clearTimeout(shadowTimeoutId);
                clearTimeout(highlightingTimeoutId);
                clearTimeout(scrollTimeoutId);
            };
        }
    }, [mostRecentlyUpdatedModel, reset, className, scrollToHighlightedItem, tableRef, resetFunction]);
};
export default useTableHighlighting;
